"use strict";

/**
 * Copyright (c) "Neo4j"
 * Neo4j Sweden AB [http://neo4j.com]
 *
 * This file is part of Neo4j.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
var __importDefault = this && this.__importDefault || function (mod) {
  return mod && mod.__esModule ? mod : {
    "default": mod
  };
};
Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.WRITE = exports.READ = exports.Driver = void 0;
var bookmark_1 = require("./internal/bookmark");
var connectivity_verifier_1 = require("./internal/connectivity-verifier");
var configured_custom_resolver_1 = __importDefault(require("./internal/resolver/configured-custom-resolver"));
var constants_1 = require("./internal/constants");
var logger_1 = require("./internal/logger");
var session_1 = __importDefault(require("./session"));
var util_1 = require("./internal/util");
var DEFAULT_MAX_CONNECTION_LIFETIME = 60 * 60 * 1000; // 1 hour
/**
 * The default record fetch size. This is used in Bolt V4 protocol to pull query execution result in batches.
 * @type {number}
 */
var DEFAULT_FETCH_SIZE = 1000;
/**
 * Constant that represents read session access mode.
 * Should be used like this: `driver.session({ defaultAccessMode: neo4j.session.READ })`.
 * @type {string}
 */
var READ = constants_1.ACCESS_MODE_READ;
exports.READ = READ;
/**
 * Constant that represents write session access mode.
 * Should be used like this: `driver.session({ defaultAccessMode: neo4j.session.WRITE })`.
 * @type {string}
 */
var WRITE = constants_1.ACCESS_MODE_WRITE;
exports.WRITE = WRITE;
var idGenerator = 0;
/**
 * A driver maintains one or more {@link Session}s with a remote
 * Neo4j instance. Through the {@link Session}s you can send queries
 * and retrieve results from the database.
 *
 * Drivers are reasonably expensive to create - you should strive to keep one
 * driver instance around per Neo4j Instance you connect to.
 *
 * @access public
 */
var Driver = /** @class */function () {
  /**
   * You should not be calling this directly, instead use {@link driver}.
   * @constructor
   * @protected
   * @param {Object} meta Metainformation about the driver
   * @param {Object} config
   * @param {function(id: number, config:Object, log:Logger, hostNameResolver: ConfiguredCustomResolver): ConnectionProvider } createConnectonProvider Creates the connection provider
   * @param {function(args): Session } createSession Creates the a session
  */
  function Driver(meta, config, createConnectonProvider, createSession) {
    if (config === void 0) {
      config = {};
    }
    if (createSession === void 0) {
      createSession = function createSession(args) {
        return new session_1.default(args);
      };
    }
    sanitizeConfig(config);
    validateConfig(config);
    this._id = idGenerator++;
    this._meta = meta;
    this._config = config;
    this._log = logger_1.Logger.create(config);
    this._createConnectionProvider = createConnectonProvider;
    this._createSession = createSession;
    /**
     * Reference to the connection provider. Initialized lazily by {@link _getOrCreateConnectionProvider}.
     * @type {ConnectionProvider}
     * @protected
     */
    this._connectionProvider = null;
    this._afterConstruction();
  }
  /**
   * Verifies connectivity of this driver by trying to open a connection with the provided driver options.
   *
   * @public
   * @param {Object} param - The object parameter
   * @param {string} param.database - The target database to verify connectivity for.
   * @returns {Promise<ServerInfo>} promise resolved with server info or rejected with error.
   */
  Driver.prototype.verifyConnectivity = function (_a) {
    var _b = _a === void 0 ? {} : _a,
      _c = _b.database,
      database = _c === void 0 ? '' : _c;
    var connectionProvider = this._getOrCreateConnectionProvider();
    var connectivityVerifier = new connectivity_verifier_1.ConnectivityVerifier(connectionProvider);
    return connectivityVerifier.verify({
      database: database
    });
  };
  /**
   * Returns whether the server supports multi database capabilities based on the protocol
   * version negotiated via handshake.
   *
   * Note that this function call _always_ causes a round-trip to the server.
   *
   * @returns {Promise<boolean>} promise resolved with a boolean or rejected with error.
   */
  Driver.prototype.supportsMultiDb = function () {
    var connectionProvider = this._getOrCreateConnectionProvider();
    return connectionProvider.supportsMultiDb();
  };
  /**
   * Returns whether the server supports transaction config capabilities based on the protocol
   * version negotiated via handshake.
   *
   * Note that this function call _always_ causes a round-trip to the server.
   *
   * @returns {Promise<boolean>} promise resolved with a boolean or rejected with error.
   */
  Driver.prototype.supportsTransactionConfig = function () {
    var connectionProvider = this._getOrCreateConnectionProvider();
    return connectionProvider.supportsTransactionConfig();
  };
  /**
   * Returns whether the server supports user impersonation capabilities based on the protocol
   * version negotiated via handshake.
   *
   * Note that this function call _always_ causes a round-trip to the server.
   *
   * @returns {Promise<boolean>} promise resolved with a boolean or rejected with error.
   */
  Driver.prototype.supportsUserImpersonation = function () {
    var connectionProvider = this._getOrCreateConnectionProvider();
    return connectionProvider.supportsUserImpersonation();
  };
  /**
   * @protected
   * @returns {boolean}
   */
  Driver.prototype._supportsRouting = function () {
    return this._meta.routing;
  };
  /**
   * Returns boolean to indicate if driver has been configured with encryption enabled.
   *
   * @protected
   * @returns {boolean}
   */
  Driver.prototype._isEncrypted = function () {
    return this._config.encrypted === util_1.ENCRYPTION_ON;
  };
  /**
   * Returns the configured trust strategy that the driver has been configured with.
   *
   * @protected
   * @returns {TrustStrategy}
   */
  Driver.prototype._getTrust = function () {
    return this._config.trust;
  };
  /**
   * Acquire a session to communicate with the database. The session will
   * borrow connections from the underlying connection pool as required and
   * should be considered lightweight and disposable.
   *
   * This comes with some responsibility - make sure you always call
   * {@link close} when you are done using a session, and likewise,
   * make sure you don't close your session before you are done using it. Once
   * it is closed, the underlying connection will be released to the connection
   * pool and made available for others to use.
   *
   * @public
   * @param {Object} param - The object parameter
   * @param {string} param.defaultAccessMode=WRITE - The access mode of this session, allowed values are {@link READ} and {@link WRITE}.
   * @param {string|string[]} param.bookmarks - The initial reference or references to some previous
   * transactions. Value is optional and absence indicates that that the bookmarks do not exist or are unknown.
   * @param {number} param.fetchSize - The record fetch size of each batch of this session.
   * Use {@link FETCH_ALL} to always pull all records in one batch. This will override the config value set on driver config.
   * @param {string} param.database - The database this session will operate on.
   * @param {string} param.impersonatedUser - The username which the user wants to impersonate for the duration of the session.
   * @return {Session} new session.
   */
  Driver.prototype.session = function (_a) {
    var _b = _a === void 0 ? {} : _a,
      _c = _b.defaultAccessMode,
      defaultAccessMode = _c === void 0 ? WRITE : _c,
      bookmarkOrBookmarks = _b.bookmarks,
      _d = _b.database,
      database = _d === void 0 ? '' : _d,
      impersonatedUser = _b.impersonatedUser,
      fetchSize = _b.fetchSize;
    return this._newSession({
      defaultAccessMode: defaultAccessMode,
      bookmarkOrBookmarks: bookmarkOrBookmarks,
      database: database,
      reactive: false,
      impersonatedUser: impersonatedUser,
      fetchSize: validateFetchSizeValue(fetchSize, this._config.fetchSize)
    });
  };
  /**
   * Close all open sessions and other associated resources. You should
   * make sure to use this when you are done with this driver instance.
   * @public
   * @return {Promise<void>} promise resolved when the driver is closed.
   */
  Driver.prototype.close = function () {
    this._log.info("Driver " + this._id + " closing");
    if (this._connectionProvider) {
      return this._connectionProvider.close();
    }
    return Promise.resolve();
  };
  /**
   * @protected
   */
  Driver.prototype._afterConstruction = function () {
    this._log.info(this._meta.typename + " driver " + this._id + " created for server address " + this._meta.address);
  };
  /**
   * @private
   */
  Driver.prototype._newSession = function (_a) {
    var defaultAccessMode = _a.defaultAccessMode,
      bookmarkOrBookmarks = _a.bookmarkOrBookmarks,
      database = _a.database,
      reactive = _a.reactive,
      impersonatedUser = _a.impersonatedUser,
      fetchSize = _a.fetchSize;
    var sessionMode = session_1.default._validateSessionMode(defaultAccessMode);
    var connectionProvider = this._getOrCreateConnectionProvider();
    var bookmark = bookmarkOrBookmarks ? new bookmark_1.Bookmark(bookmarkOrBookmarks) : bookmark_1.Bookmark.empty();
    return this._createSession({
      mode: sessionMode,
      database: database || '',
      connectionProvider: connectionProvider,
      bookmark: bookmark,
      config: this._config,
      reactive: reactive,
      impersonatedUser: impersonatedUser,
      fetchSize: fetchSize
    });
  };
  /**
   * @private
   */
  Driver.prototype._getOrCreateConnectionProvider = function () {
    if (!this._connectionProvider) {
      this._connectionProvider = this._createConnectionProvider(this._id, this._config, this._log, createHostNameResolver(this._config));
    }
    return this._connectionProvider;
  };
  return Driver;
}();
exports.Driver = Driver;
/**
 * @private
 * @returns {Object} the given config.
 */
function validateConfig(config) {
  var resolver = config.resolver;
  if (resolver && typeof resolver !== 'function') {
    throw new TypeError("Configured resolver should be a function. Got: " + resolver);
  }
  return config;
}
/**
 * @private
 */
function sanitizeConfig(config) {
  config.maxConnectionLifetime = sanitizeIntValue(config.maxConnectionLifetime, DEFAULT_MAX_CONNECTION_LIFETIME);
  config.maxConnectionPoolSize = sanitizeIntValue(config.maxConnectionPoolSize, constants_1.DEFAULT_POOL_MAX_SIZE);
  config.connectionAcquisitionTimeout = sanitizeIntValue(config.connectionAcquisitionTimeout, constants_1.DEFAULT_POOL_ACQUISITION_TIMEOUT);
  config.fetchSize = validateFetchSizeValue(config.fetchSize, DEFAULT_FETCH_SIZE);
}
/**
 * @private
 */
function sanitizeIntValue(rawValue, defaultWhenAbsent) {
  var sanitizedValue = parseInt(rawValue, 10);
  if (sanitizedValue > 0 || sanitizedValue === 0) {
    return sanitizedValue;
  } else if (sanitizedValue < 0) {
    return Number.MAX_SAFE_INTEGER;
  } else {
    return defaultWhenAbsent;
  }
}
/**
 * @private
 */
function validateFetchSizeValue(rawValue, defaultWhenAbsent) {
  var fetchSize = parseInt(rawValue, 10);
  if (fetchSize > 0 || fetchSize === constants_1.FETCH_ALL) {
    return fetchSize;
  } else if (fetchSize === 0 || fetchSize < 0) {
    throw new Error("The fetch size can only be a positive value or " + constants_1.FETCH_ALL + " for ALL. However fetchSize = " + fetchSize);
  } else {
    return defaultWhenAbsent;
  }
}
/**
 * @private
 * @returns {ConfiguredCustomResolver} new custom resolver that wraps the passed-in resolver function.
 *              If resolved function is not specified, it defaults to an identity resolver.
 */
function createHostNameResolver(config) {
  return new configured_custom_resolver_1.default(config.resolver);
}
exports.default = Driver;